// @flow
import { useContext, useEffect, useState } from 'react';
import routerContext from './RouterContext';
import type { Route, BeforeNavigationArgs } from './types';

type UseBeforeNavigation = [
  /**
   * If non-null, navigation is stopped and this is the route that would be navigated to.
   */
  ?Route,
  /**
   * A callback to resume the stopped navigation
   */
  () => void,
  /**
   * A callback to cancel the stopped navigation
   */
  () => void,
];

/**
 * A React hook to stop and resume SPA navigation. This can be used sparingly for confirmation
 * dialogs.
 *
 * This only applies to SPA navigation. To stop full-reloading browser navigation, use an
 * onbeforeunload event listener.
 *
 * @param shouldStop A predicate called when navigation occurs. If this returns true, the
 * the navigation is temporarily stopped.
 */
const useBeforeNavigation = (
  shouldStop: (to: Route) => boolean = () => true,
): UseBeforeNavigation => {
  const { addBeforeNavigationCallback, removeBeforeNavigationCallback } = useContext(routerContext);

  const [callbackArgs, setCallbackArgs] = useState<?BeforeNavigationArgs>();

  useEffect(() => {
    const handleBeforeNavigation = args => {
      if (shouldStop(args.to)) {
        setCallbackArgs(args);
        return true;
      }

      return false;
    };

    addBeforeNavigationCallback(handleBeforeNavigation);
    return () => removeBeforeNavigationCallback(handleBeforeNavigation);
  }, [shouldStop, addBeforeNavigationCallback, removeBeforeNavigationCallback]);

  if (callbackArgs != null) {
    const { to, continueNavigation } = callbackArgs;

    const continueNavigationCallback = () => {
      setCallbackArgs(null);
      continueNavigation();
    };

    const cancelNavigationCallback = () => {
      setCallbackArgs(null);
    };

    return [to, continueNavigationCallback, cancelNavigationCallback];
  }

  return [null, () => {}, () => {}];
};

export default useBeforeNavigation;
